// ──────────────────────────────────────────────────
// Records Module — TypeScript Types
// ──────────────────────────────────────────────────

// FDI tooth numbers — Adult dentition (11–48)
export type FDINumber = number; // 11-18, 21-28, 31-38, 41-48

export const UPPER_TEETH: FDINumber[] = [18,17,16,15,14,13,12,11,21,22,23,24,25,26,27,28];
export const LOWER_TEETH: FDINumber[] = [48,47,46,45,44,43,42,41,31,32,33,34,35,36,37,38];
export const ALL_TEETH: FDINumber[] = [...UPPER_TEETH, ...LOWER_TEETH];

export type Surface = 'mesial' | 'distal' | 'occlusal' | 'buccal' | 'lingual' | 'labial' | 'palatal';

// ── NGAP ──

export interface TreatmentCategory {
  id: string;
  name: string;
  name_ar: string;
  name_en: string;
  code: string;
  color: string;
  icon: string;
  icon_image: string | null;
  sort_order: number;
  is_active: boolean;
  acts?: NGAPAct[];
}

export interface NGAPAct {
  id: string;
  code: string;
  label: string;
  label_ar: string;
  label_en: string;
  category_id: string;
  category_name: string;
  category_code: string;
  category_color: string;
  category_icon_image?: string | null;
  icon_image?: string | null;
  coefficient: number;
  default_price: number;
  chart_state_code: string;
  target_zone: string;
  duration_minutes: number | null;
  requires_tooth: boolean;
  is_active: boolean;
}

export interface NGAPActCompact {
  id: string;
  code: string;
  label: string;
  category_name: string;
  category_color: string;
  category_icon_image?: string | null;
  icon_image?: string | null;
  coefficient: number;
  chart_state_code: string;
  target_zone: string;
  requires_tooth: boolean;
}

// ── Dental Chart ──

export interface ToothLayer {
  code: string;        // e.g. CARIES, CROWN, RCT, EXTRACT
  target_zone: string; // WHOLE_TOOTH, CROWN_SURFACE, ROOT, NECK, etc.
  surfaces: string[];
  date_added: string;
  treatment_id?: string;
  metadata: Record<string, unknown>;
}

export interface ToothData {
  base_state: string;  // PRESENT, MISSING, EXTRACT, IMPACTED
  layers: ToothLayer[];
}

export interface DentalChartData {
  id: string;
  patient_id: string;
  patient_name: string;
  teeth_data: Record<string, ToothData>;
  created_at: string;
  updated_at: string;
}

// ── Clinical Treatment ──

export type TreatmentStatus = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type TreatmentPriority = 'urgent' | 'high' | 'medium' | 'low';

export interface ClinicalTreatment {
  id: string;
  patient_id: string;
  patient_name: string;
  ngap_act_id: string | null;
  act_code: string;
  act_label: string;
  category_name: string;
  tooth_number: number | null;
  surfaces: string;
  status: TreatmentStatus;
  status_display: string;
  priority: TreatmentPriority;
  visit_id: string | null;
  appointment_id: string | null;
  dentist_id: string | null;
  dentist_name: string;
  amount: number;
  coefficient_used: number;
  planned_date: string | null;
  completed_date: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTreatmentRequest {
  patient_id: string;
  ngap_act_id?: string;
  treatment_type_id?: string;
  tooth_number?: number | null;
  surfaces?: string;
  status?: TreatmentStatus;
  priority?: TreatmentPriority;
  notes?: string;
  amount?: number;
  visit_id?: string;
  planned_date?: string;
  completed_date?: string;
}

// ── Visit / Séance ──

export type SeanceStatus = 'OPEN' | 'CLOSED';

export interface VisitRecord {
  id: string;
  patient_id: string;
  patient_name: string;
  appointment_id: string | null;
  visit_date: string;
  visit_type: string;
  chief_complaint: string;
  clinical_notes: string;
  next_appointment_needed: boolean;
  // Séance fields
  status: SeanceStatus;
  closed_at: string | null;
  amount_paid: number;
  amount_due: number;
  amount_remaining: number;
  // Dentist
  dentist_id: string | null;
  dentist_name: string;
  treatments: ClinicalTreatment[];
  total_cost: number;
  created_at: string;
  updated_at: string;
}

// ── Medical Record ──

export interface MedicalRecord {
  id: string;
  patient_id: string;
  record_type: string;
  record_type_display: string;
  description: string;
  severity: string;
  date: string | null;
  is_active: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

// ── Document ──

export interface PatientDocument {
  id: string;
  patient_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  content_type: string;
  category: string;
  category_display: string;
  tooth_number: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

// ── Summary ──

export interface PatientRecordsSummary {
  patient_id: string;
  patient_name: string;
  total_treatments: number;
  total_planned: number;
  total_completed: number;
  total_documents: number;
  total_records: number;
  total_medical_records: number;
  last_visit: string | null;
  has_dental_chart: boolean;
  medical_conditions: string[];
  allergies: string[];
  medications: string[];
}

// ── Tooth History ──

export interface ToothHistory {
  tooth_number: number;
  patient_name: string;
  chart_data: ToothData;
  treatments: ClinicalTreatment[];
}

// ── Dental State Configuration (matches dental-chart-embed.html DENTAL_STATES) ──

export type TargetZone = 'WHOLE_TOOTH' | 'CROWN_SURFACE' | 'CROWN_WHOLE' | 'ROOT' | 'NECK';

export type StateCategory =
  | 'Anatomy & Status'
  | 'Pathology & Restoration'
  | 'Endodontics & Roots'
  | 'Periodontal / Neck'
  | 'Surgery';

export interface DentalStateConfig {
  code: string;
  label: string;
  label_fr: string;
  category: StateCategory;
  targetZones: TargetZone[];
  color: string;
  icon?: string;
  destructive?: boolean; // clears layers when set as base_state
}

export const DENTAL_STATES_CONFIG: Record<string, DentalStateConfig> = {
  // ── Anatomy & Status ──
  PRESENT:     { code: 'PRESENT',     label: 'Sound/Healthy',      label_fr: 'Saine',             category: 'Anatomy & Status',        targetZones: ['WHOLE_TOOTH'], color: '#4CAF50', icon: '✓', destructive: true },
  MISSING:     { code: 'MISSING',     label: 'Missing',            label_fr: 'Absente',            category: 'Anatomy & Status',        targetZones: ['WHOLE_TOOTH'], color: '#9E9E9E', icon: '∅', destructive: true },
  IMPACTED:    { code: 'IMPACTED',    label: 'Impacted',           label_fr: 'Incluse',            category: 'Anatomy & Status',        targetZones: ['WHOLE_TOOTH'], color: '#FF9800', icon: '⬇', destructive: true },
  ROOT_ONLY:   { code: 'ROOT_ONLY',   label: 'Retained Root',      label_fr: 'Racine résiduelle',  category: 'Anatomy & Status',        targetZones: ['WHOLE_TOOTH'], color: '#795548', icon: '⌊', destructive: true },
  MOBILE:      { code: 'MOBILE',      label: 'Mobile',             label_fr: 'Mobile',             category: 'Anatomy & Status',        targetZones: ['WHOLE_TOOTH'], color: '#FF6F00', icon: '↔' },

  // ── Pathology & Restoration ──
  CARIES:      { code: 'CARIES',      label: 'Caries (Cavity)',    label_fr: 'Carie',              category: 'Pathology & Restoration', targetZones: ['CROWN_SURFACE'], color: '#F44336', icon: '●' },
  AMALGAM:     { code: 'AMALGAM',     label: 'Amalgam Fill',       label_fr: 'Amalgame',           category: 'Pathology & Restoration', targetZones: ['CROWN_SURFACE'], color: '#607D8B', icon: '■' },
  COMPOSITE:   { code: 'COMPOSITE',   label: 'Composite Fill',     label_fr: 'Composite',          category: 'Pathology & Restoration', targetZones: ['CROWN_SURFACE'], color: '#E8E8E8', icon: '□' },
  FRACTURE:    { code: 'FRACTURE',    label: 'Fractured',          label_fr: 'Fracture',           category: 'Pathology & Restoration', targetZones: ['CROWN_SURFACE', 'CROWN_WHOLE'], color: '#E91E63', icon: '⚡' },
  SEALANT:     { code: 'SEALANT',     label: 'Sealant',            label_fr: 'Scellement',         category: 'Pathology & Restoration', targetZones: ['CROWN_SURFACE'], color: '#00BCD4', icon: '◎' },
  CROWN:       { code: 'CROWN',       label: 'Crown (Cap)',        label_fr: 'Couronne',           category: 'Pathology & Restoration', targetZones: ['CROWN_WHOLE'], color: '#FFD700', icon: '♛' },
  VENEER:      { code: 'VENEER',      label: 'Veneer',             label_fr: 'Facette',            category: 'Pathology & Restoration', targetZones: ['CROWN_WHOLE'], color: '#E1BEE7', icon: '◧' },
  TEMP_FILL:   { code: 'TEMP_FILL',   label: 'Temporary Fill',     label_fr: 'Obturation temp.',   category: 'Pathology & Restoration', targetZones: ['CROWN_WHOLE', 'CROWN_SURFACE'], color: '#FFC107', icon: '◌' },

  // ── Endodontics & Roots ──
  RCT:         { code: 'RCT',         label: 'Root Canal',         label_fr: 'Traitement canalaire', category: 'Endodontics & Roots',  targetZones: ['ROOT'], color: '#673AB7', icon: '↓' },
  ABSCESS:     { code: 'ABSCESS',     label: 'Abscess',            label_fr: 'Abcès',             category: 'Endodontics & Roots',     targetZones: ['ROOT'], color: '#9C27B0', icon: '◉' },
  POST:        { code: 'POST',        label: 'Post',               label_fr: 'Tenon',             category: 'Endodontics & Roots',     targetZones: ['ROOT'], color: '#5E35B1', icon: '⇑' },
  APICOECTOMY: { code: 'APICOECTOMY', label: 'Apicoectomy',        label_fr: 'Apicectomie',        category: 'Endodontics & Roots',     targetZones: ['ROOT'], color: '#7B1FA2', icon: '✂' },
  IMPLANT:     { code: 'IMPLANT',     label: 'Implant',            label_fr: 'Implant',           category: 'Endodontics & Roots',     targetZones: ['ROOT'], color: '#1976D2', icon: '⬆' },

  // ── Periodontal / Neck ──
  CALCULUS:        { code: 'CALCULUS',        label: 'Tartar/Calculus',  label_fr: 'Tartre',         category: 'Periodontal / Neck',  targetZones: ['NECK'], color: '#FFEB3B', icon: '≋' },
  RECESSION:       { code: 'RECESSION',       label: 'Recession',        label_fr: 'Récession',      category: 'Periodontal / Neck',  targetZones: ['NECK'], color: '#FF7043', icon: '⌄' },
  CERVICAL_CARIES: { code: 'CERVICAL_CARIES', label: 'Cervical Caries',  label_fr: 'Carie cervicale', category: 'Periodontal / Neck', targetZones: ['NECK'], color: '#D32F2F', icon: '◐' },

  // ── Surgery ──
  EXTRACT:   { code: 'EXTRACT',   label: 'To Extract',              label_fr: 'À extraire',         category: 'Surgery', targetZones: ['WHOLE_TOOTH'], color: '#D32F2F', icon: '✕', destructive: true },
  PONTIC:    { code: 'PONTIC',    label: 'Pontic (Fake Tooth)',     label_fr: 'Pontique',           category: 'Surgery', targetZones: ['WHOLE_TOOTH'], color: '#C0C0C0', icon: '◇', destructive: true },
  ABUTMENT:  { code: 'ABUTMENT',  label: 'Abutment (Bridge Sup.)',  label_fr: 'Pilier de bridge',   category: 'Surgery', targetZones: ['WHOLE_TOOTH'], color: '#9E9E9E', icon: '◆', destructive: true },
  BRIDGE:    { code: 'BRIDGE',    label: 'Bridge (Auto)',           label_fr: 'Bridge (Auto)',      category: 'Surgery', targetZones: ['WHOLE_TOOTH'], color: '#9E9E9E', icon: '⌒', destructive: true },
};

// State categories with display info (ordered)
export const STATE_CATEGORIES: { key: StateCategory; label: string; label_fr: string; color: string }[] = [
  { key: 'Anatomy & Status',        label: 'Anatomy & Status',        label_fr: 'Anatomie & État',            color: '#4CAF50' },
  { key: 'Pathology & Restoration',  label: 'Pathology & Restoration', label_fr: 'Pathologie & Restauration',  color: '#F44336' },
  { key: 'Endodontics & Roots',      label: 'Endodontics & Roots',    label_fr: 'Endodontie & Racines',       color: '#673AB7' },
  { key: 'Periodontal / Neck',       label: 'Periodontal / Neck',     label_fr: 'Parodontal / Collet',        color: '#FF7043' },
  { key: 'Surgery',                  label: 'Surgery',                label_fr: 'Chirurgie',                  color: '#D32F2F' },
];

// Helper: get states for a category
export function getStatesForCategory(category: StateCategory): DentalStateConfig[] {
  return Object.values(DENTAL_STATES_CONFIG).filter(s => s.category === category);
}

// Helper: get the primary targetZone for a state
export function getPrimaryTargetZone(stateCode: string): TargetZone {
  return DENTAL_STATES_CONFIG[stateCode]?.targetZones[0] ?? 'WHOLE_TOOTH';
}

// ── Legacy CHART_STATES (kept for backward compatibility) ──
export const CHART_STATES: Record<string, { label: string; color: string; icon?: string }> = Object.fromEntries(
  Object.entries(DENTAL_STATES_CONFIG).map(([k, v]) => [k, { label: v.label, color: v.color, icon: v.icon }])
);

// Status colors for UI
export const STATUS_COLORS: Record<TreatmentStatus, { bg: string; text: string; border: string }> = {
  PLANNED:     { bg: 'bg-blue-50 dark:bg-blue-900/20',   text: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-200 dark:border-blue-800' },
  IN_PROGRESS: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300',   border: 'border-amber-200 dark:border-amber-800' },
  COMPLETED:   { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300',   border: 'border-green-200 dark:border-green-800' },
  CANCELLED:   { bg: 'bg-gray-50 dark:bg-gray-900/20',   text: 'text-gray-500 dark:text-gray-400',     border: 'border-gray-200 dark:border-gray-700' },
};

export const PRIORITY_COLORS: Record<TreatmentPriority, string> = {
  urgent: 'text-red-600 dark:text-red-400',
  high:   'text-orange-600 dark:text-orange-400',
  medium: 'text-blue-600 dark:text-blue-400',
  low:    'text-gray-500 dark:text-gray-400',
};
